import moment from 'jalali-moment';

/** Always latin-digit moment so HH:mm comparisons survive a global fa locale. */
const at = (timestamp: number) => moment(timestamp * 1000).locale('en');

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const ARABIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

export const JALALI_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
export const JALALI_WEEK_DAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const JALALI_WEEK_DAYS_FULL = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];

export const toEnglishDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, d => String(PERSIAN_DIGITS.indexOf(d)))
    .replace(/[٠-٩]/g, d => String(ARABIC_DIGITS.indexOf(d)));

export const toFaNumber = (value: number | string) => toEnglishDigits(String(value)).replace(/[0-9]/g, d => PERSIAN_DIGITS[Number(d)]);

/** moment day (0 = یکشنبه … 6 = شنبه) -> index into JALALI_WEEK_DAYS (0 = شنبه … 6 = جمعه) */
export const momentDayToJalaliIndex = (momentDay: number) => (momentDay === 6 ? 0 : momentDay + 1);

export const jalaliToTimestamp = (year: number, month: number, day: number, hour = 0, minute = 0): number =>
  Math.floor(moment(`${year}/${month}/${day} ${hour}:${minute}`, 'jYYYY/jMM/jDD HH:mm', 'en').valueOf() / 1000);

export const getTodayStart = (): number => Math.floor(moment().startOf('day').valueOf() / 1000);

export const getMonthRange = (year: number, month: number): { from: number; to: number } => {
  const m = moment(`${year}/${month}/1`, 'jYYYY/jMM/jDD');
  return {
    from: Math.floor(m.clone().startOf('jMonth').valueOf() / 1000),
    to: Math.floor(m.clone().endOf('jMonth').valueOf() / 1000),
  };
};

export const formatFullDate = (timestamp: number) => toFaNumber(at(timestamp).format('jYYYY/jMM/jDD'));

export const formatDateWithDay = (timestamp: number) => {
  const m = at(timestamp);
  return `${JALALI_WEEK_DAYS_FULL[momentDayToJalaliIndex(m.day())]} ${toFaNumber(m.jDate())} ${JALALI_MONTHS[m.jMonth()]}`;
};

export const formatTime = (timestamp: number) => toFaNumber(at(timestamp).format('HH:mm'));

export const isFullDayRange = (from: number, to: number) =>
  at(from).format('HH:mm') === '00:00' && at(to).format('HH:mm') === '23:59';

export const formatDateOrRange = (from: number, to: number) => {
  const f = at(from);
  const t = at(to);
  return f.isSame(t, 'day') ? f.format('jYYYY/jMM/jDD') : `${f.format('jYYYY/jMM/jDD')} - ${t.format('jYYYY/jMM/jDD')}`;
};

export const timeToMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** متن مدت تعطیلی با رند نرم */
export const formatVacationDuration = (from: number, to: number): string => {
  const f = at(from);
  const t = at(to);
  const totalMinutes = Math.max(0, Math.floor((t.valueOf() - f.valueOf()) / 60000));
  const sameDay = f.isSame(t, 'day');
  const fullDayBounds = f.format('HH:mm') === '00:00' && t.format('HH:mm') === '23:59';
  const prefix = 'مدت زمان:';

  if (fullDayBounds) {
    const days = sameDay ? 1 : t.clone().startOf('day').diff(f.clone().startOf('day'), 'days') + 1;
    return `${prefix} ${toFaNumber(days)} روز`;
  }
  if (totalMinutes >= 24 * 60) {
    return `${prefix} ${toFaNumber(Math.max(1, Math.round(totalMinutes / (24 * 60))))} روز`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  let rounded: string;
  if (hours === 0 && minutes < 15) rounded = 'حدود کمتر از ۱ ساعت';
  else if (hours === 0 && minutes < 45) rounded = 'حدود نیم ساعت';
  else if (hours === 0) rounded = 'حدود ۱ ساعت';
  else if (minutes < 15) rounded = `حدود ${toFaNumber(hours)} ساعت`;
  else if (minutes < 45) rounded = `حدود ${toFaNumber(hours)} ساعت و نیم`;
  else if (hours + 1 >= 24) rounded = 'حدود ۱ روز';
  else rounded = `حدود ${toFaNumber(hours + 1)} ساعت`;
  return `${prefix} ${rounded}`;
};

export const IRAN_MOBILE_REGEX = /^09\d{9}$/;

/** ورودی کاربر → فقط رقم، حداکثر ۱۱ */
export const normalizeIranMobileInput = (value: string) => toEnglishDigits(value).replace(/\D/g, '').slice(0, 11);

export const getIranMobileValidation = (value: string): 'empty' | 'invalid' | 'valid' => {
  const normalized = normalizeIranMobileInput(value);
  if (!normalized) return 'empty';
  return IRAN_MOBILE_REGEX.test(normalized) ? 'valid' : 'invalid';
};

/** Map of day-start unix -> holiday title */
export const buildHolidayTitleByDay = (holidays: Array<{ date: string | number; title: string }>) => {
  const map = new Map<number, string>();
  holidays.forEach(h => {
    let ts: number;
    if (typeof h.date === 'number') ts = h.date;
    else {
      const parsed = parseInt(h.date, 10);
      if (!Number.isNaN(parsed)) ts = parsed;
      else {
        const m = moment(h.date, 'jYYYY/jMM/jDD');
        if (!m.isValid()) return;
        ts = Math.floor(m.valueOf() / 1000);
      }
    }
    const day = Math.floor(at(ts).startOf('day').valueOf() / 1000);
    if (!map.has(day)) map.set(day, typeof h.title === 'string' ? h.title.trim() : '');
  });
  return map;
};

/** ?center_id= (or ?centerId=) from the page URL — the doctor home passes the chosen center this way */
export const readCenterIdFromLocation = (): string | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const value = (params.get('center_id') ?? params.get('centerId') ?? '').trim();
  return value || null;
};
